import React, { useState, useEffect, useContext } from 'react';
import '../../App.css';
import CSS from 'csstype';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

// icons
import siteIcon from '../../icons/data-centre.320.png';

// types relating to events.
import {
  Event,
  EventType,
  EventTarget,
  InitiateComputeSearchParams,
  ServiceStatusParams,
} from '../../utils/events';

// functions
import { APIPrefix } from '../../utils/functions';

// types

// classes
import Scrollbox from '../../tools/scrollbox';
import PleaseWait from '../../tools/please-wait';
import ComputeSession from '../compute-tiles/compute-session';
import SiteIcon from '../../tools/site-icon';

// import state definition and functions (redux).
import { setUpdateServiceStatus } from './search-compute-slice';
import {
  loadComputeData,
  updateComputeSearchServiceStatus,
} from './search-compute-actions';
import { ComputeServiceType, Site } from '../../utils/types';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchComputeTable() {
  //
  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------
  const dispatch = useAppDispatch();

  // state variables.
  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.searchCompute.sLoadingComponent,
  );
  const sUpdateServiceStatus: ServiceStatusParams[] = useAppSelector(
    (state) => state.searchCompute.sUpdateServiceStatus,
  );
  // the flattened list of services, including information such as node, site, and site_uuid.
  const sServicesList: any[] = useAppSelector(
    (state) => state.searchCompute.sServicesList,
  );

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Form controls are often post-fixed with A_B, where A is the
  //	site index and B is the service index. Here we split the
  //	ID and convert to numeric.
  //
  //	------------------------------------------------------------

  function getSiteAndServiceIndexes(args: { serviceID: string }) {
    // get the site index and service index.
    var siteIndex: string = '';
    var serviceIndex: string = '';
    if (args.serviceID.indexOf('_') > -1) {
      siteIndex = args.serviceID.slice(0, args.serviceID.indexOf('_'));
      serviceIndex = args.serviceID.slice(
        args.serviceID.indexOf('_') + 1,
        args.serviceID.length,
      );
    }

    let siteIndexNumeric: number = -1;
    let serviceIndexNumeric: number = -1;
    try {
      siteIndexNumeric = Number(siteIndex);
      serviceIndexNumeric = Number(serviceIndex);
    } catch (e) {}

    // return something.
    return { siteIndex: siteIndexNumeric, serviceIndex: serviceIndexNumeric };
  } // getSiteAndServiceIndexes

  //	------------------------------------------------------------
  //
  //	H O O K S
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	updates the current list of services from the state array
  //	of service-status updates.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('SSE: [sUpdateServiceStatus] hook fires [search-compute-table]');

    // loop through the array of service updates, updating the state array.
    for (let i: number = 0; i < sUpdateServiceStatus.length; i++) {
      updateComputeSearchServiceStatus(sUpdateServiceStatus[i]);
    }
    //
  }, [sUpdateServiceStatus]);

  //	------------------------------------------------------------
  //
  //	Component code
  //
  //	------------------------------------------------------------

  return (
    <div className="panel-main" style={{ flexDirection: 'column', padding: '8px' }}>
      <Scrollbox
        id="search-compute-scrollbox"
        direction="vertical"
        width="100%"
        flex="1 1"
        flexDirection="column"
      >
        {sLoadingComponent ? (
          <PleaseWait text="Loading" visible={true} />
        ) : (
          <>
            <Sites servicesList={sServicesList} />
            <div style={{ flex: '1 1 auto' }} />
          </>
        )}
      </Scrollbox>
    </div>
  );
} // SearchComputeTable

//	------------------------------------------------------------
//
//	Display an icon, description, and a launch button for
//	each item in a list of services.
//
//	------------------------------------------------------------

function Services(props: { associatedServices: ComputeServiceType[] }) {
  //
  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <div
      id="compute-container"
      style={{ display: 'block', flex: '0 0 auto', position: 'relative' }}
    >
      {props.associatedServices
        .sort((itemA, itemB) =>
          (itemA.service_name?.toLowerCase() ?? '') >
            (itemB.service_name?.toLowerCase() ?? '') ||
          (itemA.service_name === undefined &&
            itemB.service_name === undefined &&
            (itemA.id ?? 0) > (itemB.id ?? 0))
            ? 1
            : -1,
        )
        .map((hub, hubindex) => (
          <ComputeSession session={hub} />
        ))}
    </div>
  );
  //
} // Services

//	------------------------------------------------------------
//
//	Display a site, occupying the whole width of the flex box,
//	with a list of services underneath it.
//
//	------------------------------------------------------------

function SiteTile(props: {
  site?: Site;
  row: any;
  latitudeLongitude: {
    latitude: number | undefined;
    longitude: number | undefined;
  };
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	C O N T E X T   V A R I A B L E S   A N D   F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // global state variables.
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // class-level constants.
  const TABLE_COL: CSS.Properties = {
    padding: '0px 10px 0px 10px',
    border: 'none',
  };

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	build the site name. we prefix the node is it is not
  //	already prefixed to the site name.
  //
  //	------------------------------------------------------------

  function siteName(args: { row: any }) {
    //
    var name: string = 'unknown';
    var node: string | undefined = undefined;
    var site: string | undefined = undefined;

    if ('site' in args.row) {
      site = args.row['site'];
    }
    if ('node' in args.row) {
      node = args.row['node'];
    }

    if (site !== undefined) {
      name = site;
    }
    if (site === undefined && node !== undefined) {
      name = node;
    }
    if (
      site !== undefined &&
      node !== undefined &&
      !site.toUpperCase().startsWith(node.toUpperCase())
    ) {
      name = node + ' ' + site;
    }

    return name;
    //
  } // siteName

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <>
      <div
        className="text-colour"
        style={{
          flex: '0 0 auto',
          margin: '10px 5px 15px 5px',
          width: '100%',
        }}
      >
        <div
          className="src-header"
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            opacity: sG_DarkMode ? '0.3' : '1.0',
          }}
        />
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'top',
            top: '-128px',
          }}
        >
          <div style={{ flex: '0 0 5px' }} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: '10px',
            }}
          >
            <SiteIcon site={props.site} size={60} />
            <div style={{ flex: '0 0 10px' }} />
            <div
              style={{
                flex: '1 1 auto',
                display: 'flex',
                height: '90px',
                flexDirection: 'column',
              }}
            >
              <div style={{ flex: '1 1' }} />
              <div
                style={{
                  flex: '0 0 auto',
                  alignItems: 'left',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'left',
                }}
              >
                <table>
                  <tbody>
                    <tr>
                      <td style={TABLE_COL}>{t('Description')}:</td>
                      <td style={TABLE_COL} colSpan={3}>
                        {'description' in props.row ? props.row['description'] : ''}
                      </td>
                    </tr>
                    <tr>
                      <td style={TABLE_COL}>{t('Location')}:</td>
                      <td style={TABLE_COL}>
                        {props.latitudeLongitude.latitude !== undefined
                          ? props.latitudeLongitude.latitude
                          : '/'}{' '}
                        &deg; N,{' '}
                        {props.latitudeLongitude.longitude !== undefined
                          ? props.latitudeLongitude.longitude
                          : '/'}{' '}
                        &deg; E
                      </td>
                      <td style={TABLE_COL}>{t('Hardware type')}:</td>
                      <td style={TABLE_COL}>
                        {'hardware_type' in props.row ? props.row['hardware_type'] : ''}
                      </td>
                      {/*<td style = {TABLE_COL}>Middleware version:</td>
										<td style = {TABLE_COL}>{'middleware_version' in args.row ? args.row[ 'middleware_version' ] : ''}</td>*/}
                    </tr>
                    <tr>
                      <td style={TABLE_COL}>{t('Hardware capabilities')}:</td>
                      <td style={TABLE_COL} colSpan={3}>
                        {'hardware_capabilities' in props.row
                          ? props.row['hardware_capabilities']
                          : ''}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ flex: '1 1' }} />
            </div>
          </div>
          <div style={{ flex: '0 0 5px' }} />
          <div
            style={{
              flex: '0 0 auto',
              fontSize: '1.2em',
              marginLeft: '10px',
            }}
          >
            {siteName({ row: props.row })}
          </div>
        </div>
      </div>
      {'services' in props.row ? (
        Services({
          associatedServices: props.row['services'],
        })
      ) : (
        <></>
      )}
    </>
  );
} // SiteTile

//	------------------------------------------------------------
//
//	Display all the sites, and then the services underneath.
//
//	------------------------------------------------------------

function Sites(props: { servicesList: Site[] }) {
  const sServicesList: any[] = useAppSelector(
    (state) => state.searchCompute.sServicesList,
  );
  //	--------------------------------------------------------------------------
  //
  //	REDUX
  //
  //	--------------------------------------------------------------------------

  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	get the latitude and longitude for a particular site.
  //
  //	------------------------------------------------------------

  function getLatitudeLongitude(args: { item: any }) {
    //
    var latitudeLongitude: {
      latitude: number | undefined;
      longitude: number | undefined;
    } = {
      latitude: undefined,
      longitude: undefined,
    };

    // get site uuid.
    var site_uuid: string = '';
    if ('site_uuid' in args.item) {
      site_uuid = args.item['site_uuid'];
    }

    // use this site uuid to get the site details.
    const site_details = sD_sites.find(
      (s) => (s.site_uuid ?? '').toLowerCase() === site_uuid,
    );
    if (site_details) {
      latitudeLongitude.latitude = site_details.latitude;
      latitudeLongitude.longitude = site_details.longitude;
    }

    // return something.
    return latitudeLongitude;
    //
  } // getLatitudeLongitude

  //	------------------------------------------------------------
  //
  //	group services by node and site.
  //
  //	------------------------------------------------------------

  function groupServices(args: { servicesList: any[] }) {
    let groupedServices: {
      node: string;
      site: string;
      site_uuid: string;
      services: any[];
    }[] = [];

    for (let i: number = 0; i < args.servicesList.length; i++) {
      let index: number = groupedServices.findIndex(
        (item) => item.site_uuid === args.servicesList[i].site.site_uuid,
      );

      if (index === -1) {
        // add a new site
        const newSite: {
          node: string;
          site: string;
          site_uuid: string;
          services: any[];
        } = {
          node: args.servicesList[i].site.node,
          site: args.servicesList[i].site.site,
          site_uuid: args.servicesList[i].site.site_uuid,
          services: [args.servicesList[i]],
        };
        groupedServices.push(newSite);
        //
      } else {
        //
        // add item to an existing site.
        groupedServices[index].services.push(args.servicesList[i]);
        //
      }
    }

    return groupedServices;
    //
  } // groupServices

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <>
      {groupServices({ servicesList: props.servicesList })
        .sort((itemA, itemB) =>
          (itemA.node?.toLowerCase() ?? '') > (itemB.node?.toLowerCase() ?? '') ||
          ((itemA.node?.toLowerCase() ?? '') === (itemB.node?.toLowerCase() ?? '') &&
            (itemA.site?.toLowerCase() ?? '') > (itemB.site?.toLowerCase() ?? ''))
            ? 1
            : -1,
        )
        .map((item, index) => (
          <SiteTile
            key={'site_' + index.toString()}
            site={
              'site_uuid' in item
                ? sD_sites.find((find) => find.site_uuid === item.site_uuid)
                : undefined
            }
            row={item}
            latitudeLongitude={getLatitudeLongitude({ item })}
          />
        ))}
    </>
  );
  //
} // Sites
